/**
 * Resolution indexes for the reference audit — built once per audit pass.
 *
 * Package-private to the `referenceAudit*` module family. `buildResolution`
 * walks the document's symbol tables / handle-bearing records once and
 * returns case-insensitive name sets + TYPE-SPECIFIC handle sets + the
 * per-block ATTDEF handle map every checker resolves against. It ALSO emits
 * DUPLICATE_TABLE_ENTRY_NAME (F1) as a side effect via `errors`.
 *
 * P2-A — DXF handles are hexadecimal and case-insensitive; the importer
 * preserves the original casing, so a handle indexed as `A1` but referenced
 * as `a1` would byte-compare unequal. We therefore canonicalise EVERY handle
 * (both when indexing and when looking up — see `checkHandle`) to lowercase
 * via `canonHandle`.
 *
 * P2-B — handle targets are TYPE-SPECIFIC: a `layoutHandle` must point at a
 * LAYOUT, a `ownerBlockRecordHandle` at a BLOCK_RECORD, etc. Validating all
 * of them against one UNION set wrongly accepts a handle of the wrong kind,
 * so we keep separate `blockRecordHandles` / `layoutHandles` / `blockHandles`
 * sets and route each field to the correct one in `referenceAudit.structural`.
 *
 * Source of truth: docs/superpowers/plans/2026-05-30-drawing-object-graph.md
 * §4 / §5.3.
 */

import type { DrawingDocument } from "./model";
import {
  ReferenceErrorCode,
  type OwnerKind,
  type ReferenceIssue,
} from "./referenceAudit.types";

export interface Resolution {
  names: {
    layers: Set<string>;
    textStyles: Set<string>;
    dimensionStyles: Set<string>;
    multileaderStyles: Set<string>;
    blocks: Set<string>;
    annotationScales: Set<string>;
    /**
     * LTYPE (linetype) name index (case-insensitive). Built from each
     * `document.linetypes` record's `Linetype.name` FIELD — the value the
     * export path serialises — NOT the map key (Codex P2; map key and `.name`
     * can diverge after a rename or in a programmatically-built document).
     * (PR-LT2, plan §10.5.) Always carries the standard acad.lin seed + any
     * custom names; the refcheck bypasses `bylayer`/`byblock`/`continuous` via
     * `SENT_LINETYPE`.
     */
    linetypes: Set<string>;
  };
  /**
   * Type-specific handle sets (P2-B). All entries canonicalised to lowercase
   * (P2-A) — look them up with `canonHandle(handle)`.
   */
  handles: {
    /** BLOCK_RECORD entry handles. */
    blockRecords: Set<string>;
    /** LAYOUT object handles. */
    layouts: Set<string>;
    /** BLOCK (definition) handles. */
    blocks: Set<string>;
  };
  /**
   * Lowercased block name → set of that block's ATTDEF-child handles
   * (canonicalised to lowercase, P2-A).
   */
  attdefByBlock: Map<string, Set<string>>;
}

/**
 * Canonical (case-insensitive) form for a DXF handle (P2-A). Handles are
 * hexadecimal; the importer preserves original casing, so both the indexed
 * handle and the looked-up handle must be folded to the SAME case before
 * comparison. Lowercase is the chosen canonical form.
 */
export function canonHandle(handle: string): string {
  return handle.toLowerCase();
}

/**
 * Case-insensitive symbol index built from an array's `.name`; flags
 * duplicate names (case-insensitive) as DUPLICATE_TABLE_ENTRY_NAME (F1).
 *
 * P2-E — `target` is the real ARRAY path of the collection (e.g.
 * `styleTables.layers`); the emitted `path` carries the index of the
 * duplicate occurrence plus `.name`, e.g. `styleTables.layers[3].name`, so a
 * consumer can locate the offending record (the collection is an array, not a
 * name-keyed map).
 */
export function nameIndex(
  records: ReadonlyArray<{ name: string }> | undefined,
  target: string,
  field: string,
  errors: ReferenceIssue[],
): Set<string> {
  const set = new Set<string>();
  const seen = new Set<string>();
  // Defensive (heal "never throws" contract): a legal-but-dirty / not-yet-
  // schema-normalised document (e.g. a raw bridge output whose
  // `annotationScales` / symbol-table arrays are still `undefined` before
  // `assembleDrawingDocument`) must audit as if the collection were empty,
  // not crash. The whole-document import heal runs BEFORE the document is
  // fully normalised, so this is a real input shape (PR1b Phase 2a wiring).
  if (!records) return set;
  records.forEach((r, i) => {
    const k = r.name.toLowerCase();
    if (seen.has(k)) {
      errors.push({
        code: ReferenceErrorCode.DUPLICATE_TABLE_ENTRY_NAME,
        ownerKind: "styleTableRecord",
        ownerId: r.name,
        path: `${target}[${i}].name`,
        refKind: "name",
        field,
        referencedKey: r.name,
        message: `duplicate ${field} name "${r.name}"`,
      });
    }
    seen.add(k);
    set.add(k);
  });
  return set;
}

/**
 * Case-insensitive name index for a Record-keyed collection (P2-C / P2-D).
 * Two distinct record keys that fold to the same lowercased name collapse
 * into one Set entry silently; this helper detects that and emits
 * DUPLICATE_TABLE_ENTRY_NAME. `pathFor(key)` builds a path that identifies
 * the offending key (the collection is a Record, so there is no array index).
 */
export function recordNameIndex(
  keys: ReadonlyArray<string>,
  ownerKind: OwnerKind,
  field: string,
  pathFor: (key: string) => string,
  errors: ReferenceIssue[],
): Set<string> {
  const set = new Set<string>();
  const seen = new Set<string>();
  for (const key of keys) {
    const k = key.toLowerCase();
    if (seen.has(k)) {
      errors.push({
        code: ReferenceErrorCode.DUPLICATE_TABLE_ENTRY_NAME,
        ownerKind,
        ownerId: key,
        path: pathFor(key),
        refKind: "name",
        field,
        referencedKey: key,
        message: `duplicate ${field} name "${key}"`,
      });
    }
    seen.add(k);
    set.add(k);
  }
  return set;
}

/**
 * Case-insensitive name index for a Record-keyed collection whose VALUES carry
 * the authoritative name (P2-C analogue, Codex P2). Unlike `recordNameIndex`
 * — which indexes the MAP KEY — this folds each entry's `value[field]` (the
 * value that the EXPORT path serialises) into the set, while still using the
 * MAP KEY for the diagnostic `path` so a consumer can locate the offending
 * record (e.g. `linetypes["<key>"]`).
 *
 * Motivation (LTYPE): `exportLinetypes` serialises each LTYPE record by its
 * `Linetype.name` FIELD, NOT its map key. When a record's key and its `.name`
 * diverge (rename that updates the payload before re-keying, or a
 * programmatically-built document), indexing the KEY would (a) FALSE-POSITIVE a
 * reference to the real serialised name and (b) FALSE-NEGATIVE a reference to
 * the stale key that no exported record carries. Indexing the VALUE name keeps
 * the audit aligned with what export actually writes.
 *
 * Robustness (F12) — a malformed record whose `nameOf(value)` is missing/empty
 * falls back to the map key; this NEVER throws (buildResolution must stay
 * pure). Duplicate detection compares the resolved NAMES case-insensitively.
 */
export function recordValueNameIndex<V>(
  entries: ReadonlyArray<readonly [string, V]>,
  ownerKind: OwnerKind,
  field: string,
  nameOf: (value: V) => unknown,
  pathFor: (key: string) => string,
  errors: ReferenceIssue[],
): Set<string> {
  const set = new Set<string>();
  const seen = new Set<string>();
  for (const [key, value] of entries) {
    // F12 — never throw on a malformed record: fall back to the map key when
    // the value carries no usable name. `nameOf` may return any shape.
    const rawName = nameOf(value);
    const name =
      typeof rawName === "string" && rawName.length > 0 ? rawName : key;
    const k = name.toLowerCase();
    if (seen.has(k)) {
      errors.push({
        code: ReferenceErrorCode.DUPLICATE_TABLE_ENTRY_NAME,
        ownerKind,
        ownerId: name,
        path: pathFor(key),
        refKind: "name",
        field,
        referencedKey: name,
        message: `duplicate ${field} name "${name}"`,
      });
    }
    seen.add(k);
    set.add(k);
  }
  return set;
}

export function buildResolution(
  doc: DrawingDocument,
  errors: ReferenceIssue[],
): Resolution {
  // Defensive (heal "never throws" contract): the whole-document import heal
  // runs on a NOT-yet-schema-normalised document where `styleTables` and its
  // member arrays may still be `undefined` (raw bridge output). `nameIndex`
  // tolerates an undefined collection (reports it as empty) so the audit never
  // crashes on a legal-but-dirty input.
  const styleTables = doc.styleTables;
  const names = {
    layers: nameIndex(styleTables?.layers, "styleTables.layers", "layers", errors),
    textStyles: nameIndex(
      styleTables?.textStyles,
      "styleTables.textStyles",
      "textStyles",
      errors,
    ),
    dimensionStyles: nameIndex(
      styleTables?.dimensionStyles,
      "styleTables.dimensionStyles",
      "dimensionStyles",
      errors,
    ),
    multileaderStyles: nameIndex(
      styleTables?.multileaderStyles,
      "styleTables.multileaderStyles",
      "multileaderStyles",
      errors,
    ),
    // P2-C — blocks is a name-keyed Record; two keys differing only by case
    // collapse into one lowercased entry, so route through the dup-detecting
    // path. Path identifies the offending key: blocks["blocka"].
    blocks: recordNameIndex(
      Object.keys(doc.blocks ?? {}),
      "blockDefinition",
      "blocks",
      // Codex #199 P2: path must use the ORIGINAL record key (doc.blocks is
      // keyed by original case) so consumers can locate the offending record.
      (key) => `blocks[${JSON.stringify(key)}]`,
      errors,
    ),
    // P2-D — annotationScales is an array of {name}; build it through the
    // dup-detecting array path so case-insensitive duplicate scale names
    // emit DUPLICATE_TABLE_ENTRY_NAME instead of collapsing silently.
    // AutoCAD's "1:1" annotation scale is a built-in that is always present
    // and cannot be deleted; a freshly-created (or SCALELIST-less) DXF carries
    // the schema-default cannoscaleName "1:1" with an EMPTY catalogue, so we
    // always seed it implicitly (edge case 11) — otherwise every clean
    // document would false-positive on the required cannoscaleName ref.
    annotationScales: (() => {
      const set = nameIndex(
        doc.annotationScales,
        "annotationScales",
        "annotationScales",
        errors,
      );
      set.add("1:1");
      return set;
    })(),
    // P2-C analogue for LTYPE (Codex P2) — `document.linetypes` is a name-keyed
    // Record, but the EXPORT path (`exportLinetypes` / `drawingLinetypeToDxfEntry`)
    // serialises each LTYPE by its `Linetype.name` FIELD, NOT its map key. When
    // a record's key and its `.name` diverge (rename updating the payload before
    // re-keying, or a programmatically-built document), indexing the KEY would
    // both false-positive a reference to the real serialised name and
    // false-negative a reference to the now-stale key. We therefore index by the
    // VALUE name (what export writes) and keep the map KEY only for the
    // diagnostic path so a DUPLICATE_TABLE_ENTRY_NAME issue can still point at
    // the offending record location: linetypes["<key>"]. The standard seed
    // always merges in (import + schema default), so the set carries the
    // standard names even for a file with no custom LTYPE records.
    // `ownerKind: "linetype"`.
    linetypes: recordValueNameIndex(
      Object.entries(doc.linetypes ?? {}),
      "linetype",
      "linetypes",
      (lt) => (lt as { name?: unknown }).name,
      (key) => `linetypes[${JSON.stringify(key)}]`,
      errors,
    ),
  };

  // Handle sets (P2-B) — type-specific, each canonicalised to lowercase
  // (P2-A). Entities carry NO handle; only blockRecords / layouts / blocks /
  // attdef children do. attDefHandle resolves against the ATTDEF children of a
  // specific block (attdefByBlock).
  const blockRecordHandles = new Set<string>();
  const layoutHandles = new Set<string>();
  const blockHandles = new Set<string>();
  const attdefByBlock = new Map<string, Set<string>>();
  // Defensive (heal "never throws" contract): a not-yet-normalised document
  // may carry undefined `blockRecords` / `layouts` / `blocks` maps.
  for (const r of Object.values(doc.blockRecords ?? {})) {
    if (r.handle) blockRecordHandles.add(canonHandle(r.handle));
  }
  for (const l of Object.values(doc.layouts ?? {})) {
    if (l.handle) layoutHandles.add(canonHandle(l.handle));
  }
  for (const [bn, b] of Object.entries(doc.blocks ?? {})) {
    if (b.handle) blockHandles.add(canonHandle(b.handle));
    const childHandles = new Set<string>();
    for (const child of b.entities) {
      // Codex#2 P1-1: the ATTDEF discriminant is "Attdef" (model.ts), NOT
      // "ATTDEF". (INSERT = "Block", Multileader = "Multileader".)
      if (child.type === "Attdef" && child.handle) {
        childHandles.add(canonHandle(child.handle));
      }
    }
    attdefByBlock.set(bn.toLowerCase(), childHandles);
  }

  return {
    names,
    handles: {
      blockRecords: blockRecordHandles,
      layouts: layoutHandles,
      blocks: blockHandles,
    },
    attdefByBlock,
  };
}
